import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";

interface Props {
  children: ReactNode;
  /** Where the error is reported, so a blank screen is never the only trace. */
  onError: (error: Error) => void;
  reload: () => void;
}

/**
 * The last catch for a render error, so the attorney reads what happened and
 * a way back rather than a blank page. A class, because React catches render
 * errors only in a class component.
 */
export class RootErrorBoundary extends Component<Props, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    this.props.onError(error);
  }

  render(): ReactNode {
    if (!this.state.failed) {
      return this.props.children;
    }
    return (
      <Panel
        tone="destructive"
        role="alert"
        className="mx-auto mt-24 flex max-w-md flex-col items-start gap-3"
      >
        <p>
          This page stopped working. Reload to go on: what you saved is kept.
        </p>
        <Button variant="primary" onClick={this.props.reload}>
          Reload
        </Button>
      </Panel>
    );
  }
}
